import { Constants } from "../../../constants";

export const GA_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID || "";
export const FRAME_ID = "public-landing-measurement";
const HOST = "achizitii.clpstudio.ro";
const TAG_ID = "public-google-tag";
const COOKIE_SECONDS = 180 * 24 * 60 * 60;
const ENGAGEMENT_INTERVAL_MS = 10000;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
  "utm_content",
  "utm_term",
];
const LABELS = new Set([
  "demo_navigation",
  "demo_calendar",
  "sales_email",
  "sales_phone",
  "platform_preview",
]);
const CTA_EVENTS = new Set([
  "book_demo_click",
  "email_click",
  "phone_click",
  "view_platform_click",
]);
const states = new WeakMap();
const tagStates = new WeakMap();
const FRAME_URL = "/public-landing-measurement.html";

export function isAnonymousLanding(win = window) {
  try {
    const localDebug =
      ["localhost", "127.0.0.1"].includes(win.location.hostname) &&
      new URLSearchParams(win.location.search).get("analytics_debug") === "1";
    return (
      (win.location.hostname === HOST || localDebug) &&
      win.location.pathname === "/" &&
      win.localStorage.getItem(Constants.STORAGE_JWT_TOKEN_KEY) === null
    );
  } catch (_) {
    // If authentication storage cannot be checked, do not measure this visitor.
    return false;
  }
}

function stateFor(win) {
  if (!states.has(win))
    states.set(win, {
      frame: null,
      cleanup: [],
      started: false,
      bookings: new Set(),
    });
  return states.get(win);
}

export function stopPublicMeasurement(win = window) {
  const state = states.get(win);
  if (!state) return;
  if (state.frame) {
    const tagWindow = state.frame.contentWindow;
    if (tagWindow) {
      tagWindow["ga-disable-" + GA_ID] = true;
      if (tagWindow.dataLayer) tagWindow.dataLayer.length = 0;
    }
    state.frame.remove();
    state.frame = null;
  }
  state.started = false;
  if (state.queue) state.queue.length = 0;
  state.cleanup.splice(0).forEach((cleanup) => cleanup());
}

function attachQueue(tagWindow, state) {
  tagWindow.dataLayer = state.queue;
  tagWindow.gtag = function () {
    state.queue.push(arguments);
  };
}

export function publicTagWindow(win = window) {
  if (!isAnonymousLanding(win)) {
    stopPublicMeasurement(win);
    return null;
  }
  const state = stateFor(win);
  if (state.frame && state.frame.isConnected) {
    const tagWindow = state.frame.contentWindow;
    if (!tagWindow.gtag) attachQueue(tagWindow, state);
    return tagWindow;
  }
  const frame = win.document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.hidden = true;
  frame.title = "Public landing measurement";
  frame.setAttribute("aria-hidden", "true");
  frame.referrerPolicy = "no-referrer";
  frame.src = FRAME_URL;
  state.queue = [];
  state.ready = false;
  state.pendingTag = null;
  frame.addEventListener("load", () => {
    if (state.frame !== frame || !isAnonymousLanding(win)) return;
    const loadedWindow = frame.contentWindow;
    attachQueue(loadedWindow, state);
    state.ready = true;
    if (state.pendingTag) loadGoogleTag(loadedWindow, state.pendingTag);
  });
  win.document.body.appendChild(frame);
  state.frame = frame;
  // A separate browsing context destroys all Google timers/listeners on exit.
  // No Google script or dataLayer is installed in the application window.
  const tagWindow = frame.contentWindow;
  attachQueue(tagWindow, state);
  tagStates.set(tagWindow, state);
  const guard = () => {
    if (!isAnonymousLanding(win)) stopPublicMeasurement(win);
  };
  ["popstate", "storage", "visibilitychange"].forEach((name) => {
    win.addEventListener(name, guard);
    state.cleanup.push(() => win.removeEventListener(name, guard));
  });
  const leave = () => stopPublicMeasurement(win);
  win.addEventListener("pagehide", leave);
  state.cleanup.push(() => win.removeEventListener("pagehide", leave));
  ["pushState", "replaceState"].forEach((name) => {
    const original = win.history[name];
    const wrapped = function (data, unused, url) {
      if (url && new URL(url, win.location.href).pathname !== "/")
        stopPublicMeasurement(win);
      const result = original.apply(this, arguments);
      guard();
      return result;
    };
    win.history[name] = wrapped;
    state.cleanup.push(() => {
      if (win.history[name] === wrapped) win.history[name] = original;
    });
  });
  const beforeClick = (event) => {
    const anchor = event.target.closest && event.target.closest("a[href]");
    if (!anchor || (anchor.target && anchor.target !== "_self")) return;
    const url = new URL(anchor.href, win.location.href);
    if (url.origin === win.location.origin && url.pathname !== "/") leave();
  };
  win.document.addEventListener("click", beforeClick, true);
  state.cleanup.push(() =>
    win.document.removeEventListener("click", beforeClick, true),
  );
  return tagWindow;
}

export function loadGoogleTag(tagWindow, id) {
  const state = tagStates.get(tagWindow);
  if (state && !state.ready) {
    if (!state.pendingTag) state.pendingTag = id;
    return;
  }
  if (tagWindow.document.getElementById(TAG_ID)) return;
  const script = tagWindow.document.createElement("script");
  script.id = TAG_ID;
  script.async = true;
  script.referrerPolicy = "no-referrer";
  script.src = "https://www.googletagmanager.com/gtag/js?id=" + id;
  tagWindow.document.head.appendChild(script);
}

export function landingContext(win = window) {
  const url = new URL(win.location.origin + "/");
  const params = new URLSearchParams(win.location.search);
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (
      value &&
      /^[a-z0-9][a-z0-9_.-]{0,99}$/i.test(value) &&
      !/\d{7,}/.test(value)
    )
      url.searchParams.set(key, value);
  });
  let referrer = "";
  try {
    const ref = new URL(win.document.referrer);
    if (["http:", "https:"].includes(ref.protocol)) referrer = ref.origin + "/";
  } catch (_) {
    /* No referring website. */
  }
  return {
    page_location: url.href,
    page_referrer: referrer,
    page_title: "Achiziții Publice — CLP Studio",
  };
}

function send(name, params, win) {
  const state = states.get(win);
  if (!state || !state.started) return false;
  const tagWindow = publicTagWindow(win);
  if (!tagWindow || win["ga-disable-" + GA_ID] === true) return false;
  tagWindow.gtag("event", name, {
    ...landingContext(win),
    ...params,
    send_to: GA_ID,
  });
  return true;
}

export function recordLandingBooking(uid, win = window) {
  if (
    !isAnonymousLanding(win) ||
    typeof uid !== "string" ||
    !/^[a-zA-Z0-9_-]{6,100}$/.test(uid)
  )
    return false;
  const state = stateFor(win);
  if (state.bookings.has(uid)) return false;
  if (
    !send(
      "generate_lead",
      { method: "cal_com", lead_type: "demo_booking" },
      win,
    )
  )
    return false;
  state.bookings.add(uid);
  return true;
}

export function startPublicAnalytics(win = window) {
  if (
    !/^G-[A-Z0-9]+$/.test(GA_ID) ||
    win["ga-disable-" + GA_ID] === true ||
    !isAnonymousLanding(win)
  )
    return;
  const state = stateFor(win);
  if (state.started) return;
  const tagWindow = publicTagWindow(win);
  state.started = true;
  tagWindow.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  tagWindow.gtag("js", new Date());
  tagWindow.gtag("config", GA_ID, {
    ...landingContext(win),
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_domain: HOST,
    cookie_expires: COOKIE_SECONDS,
    cookie_update: false,
    ...(win.location.hostname !== HOST
      ? { debug_mode: true, cookie_domain: "none" }
      : {}),
  });
  send("page_view", {}, win);
  loadGoogleTag(tagWindow, GA_ID);
  const click = (event) => {
    const target =
      event.target.closest && event.target.closest("[data-analytics-event]");
    if (
      target &&
      target.closest(".lp") &&
      CTA_EVENTS.has(target.dataset.analyticsEvent) &&
      LABELS.has(target.dataset.analyticsLabel)
    ) {
      send(
        target.dataset.analyticsEvent,
        { cta_label: target.dataset.analyticsLabel },
        win,
      );
    }
    const anchor = event.target.closest && event.target.closest(".lp a[href]");
    if (!anchor) return;
    const url = new URL(anchor.href, win.location.href);
    if (!["http:", "https:"].includes(url.protocol)) return;
    const link = { link_url: url.origin + "/", link_domain: url.hostname };
    const file = url.pathname.match(/\.(pdf|csv|docx?|xlsx?|pptx?|zip|txt)$/i);
    if (file)
      send(
        "file_download",
        { ...link, file_extension: file[1].toLowerCase() },
        win,
      );
    if (url.hostname !== win.location.hostname)
      send("click", { ...link, outbound: true }, win);
  };
  let scrolled = false;
  const scroll = () => {
    const height = win.document.documentElement.scrollHeight;
    if (
      !scrolled &&
      height > win.innerHeight &&
      (win.scrollY + win.innerHeight) / height >= 0.9
    ) {
      scrolled = send("scroll", { percent_scrolled: 90 }, win);
    }
  };
  win.document.addEventListener("click", click);
  win.addEventListener("scroll", scroll, { passive: true });
  // The hidden frame cannot measure focus on the visible landing page itself.
  // Report only time spent on the focused, visible public page; cleanup cancels it.
  const focused = () => !win.document.hidden && win.document.hasFocus();
  let lastTick = Date.now();
  let wasFocused = focused();
  let engagementMs = 0;
  const accrueEngagement = () => {
    const now = Date.now();
    if (wasFocused) engagementMs += Math.max(0, now - lastTick);
    lastTick = now;
    wasFocused = focused();
  };
  const engagementTimer = win.setInterval(() => {
    accrueEngagement();
    if (
      engagementMs > 0 &&
      send("user_engagement", { engagement_time_msec: engagementMs }, win)
    )
      engagementMs = 0;
  }, ENGAGEMENT_INTERVAL_MS);
  ["focus", "blur", "visibilitychange"].forEach((name) => {
    win.addEventListener(name, accrueEngagement);
    state.cleanup.push(() => win.removeEventListener(name, accrueEngagement));
  });
  state.cleanup.push(
    () => win.clearInterval(engagementTimer),
    () => win.document.removeEventListener("click", click),
    () => win.removeEventListener("scroll", scroll),
  );
}
